import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { PayoutAvatar } from "@/components/admin/payouts/primitives";
import { EmptyState } from "@/components/admin/payouts/EmptyState";
import { SubscriptionsSkeleton } from "./Skeletons";
import {
  fmtDate,
  MoneyValue,
  MobileField,
  METHOD_LABEL,
  PLAN_LABEL,
  Pill,
  STATUS_BADGE,
  Th,
} from "./subscription-ui";
import { cn } from "@/lib/utils";
import { DownloadSimple, FilePdf, Receipt } from "@phosphor-icons/react";
import type { LedgerListRow, SubscriptionLedgerStatus } from "@/types";
import { throwIfNotOk } from "@/lib/api-error";

type StatusFilter = SubscriptionLedgerStatus | "all";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Sve" },
  { value: "completed", label: "Plaćeno" },
  { value: "pending", label: "Na čekanju" },
  { value: "failed", label: "Neuspjelo" },
  { value: "refunded", label: "Refundirano" },
];

export function LedgerTable() {
  const [status, setStatus] = useState<StatusFilter>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "subscription-ledger", status],
    queryFn: async () => {
      const url = new URL("/api/admin/subscription-ledger", window.location.origin);
      if (status !== "all") url.searchParams.set("status", status);
      const res = await fetch(url.toString());
      await throwIfNotOk(res, "Greška pri učitavanju podataka o pretplatama");
      return res.json() as Promise<{ rows: LedgerListRow[] }>;
    },
    placeholderData: (prev) => prev,
  });

  const rows = data?.rows ?? [];

  function handleExport() {
    const url = new URL(
      "/api/admin/subscription-ledger/csv",
      window.location.origin,
    );
    if (status !== "all") url.searchParams.set("status", status);
    window.open(url.toString(), "_blank");
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Filters */}
      <div className="rounded-2xl border border-border/60 bg-card px-4 py-4 shadow-sm sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <SegmentedFilter
            ariaLabel="Filter po statusu uplate"
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            className="w-full sm:w-auto"
          >
            <DownloadSimple className="size-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading && rows.length === 0 ? (
        <SubscriptionsSkeleton columns={8} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Nema uplata"
          description={
            status !== "all"
              ? "Pokušajte sa drugim statusom."
              : "Još nije evidentirana nijedna uplata."
          }
          hasFilters={status !== "all"}
          onClear={() => setStatus("all")}
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm lg:block">
            <table className="w-full table-fixed text-[13px]">
              <colgroup>
                <col className="w-[124px]" />
                <col />
                <col className="w-[210px]" />
                <col className="w-[150px]" />
                <col className="w-[130px]" />
                <col className="w-[80px]" />
              </colgroup>
              <thead>
                <tr className="bg-muted/40">
                  <Th className="pl-4">Datum</Th>
                  <Th>Terapeut</Th>
                  <Th>Plan i period</Th>
                  <Th align="right">Iznos</Th>
                  <Th>Status</Th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const name = r.therapistName ?? r.therapistFullName;
                  return (
                    <tr
                      key={r.id}
                      className={cn(
                        "transition-colors hover:bg-primary/[0.03]",
                        i !== rows.length - 1 && "border-b border-border/40",
                      )}
                    >
                      <td className="whitespace-nowrap px-3 py-3 pl-4">
                        <div className="font-medium leading-tight tabular-nums text-foreground">
                          {fmtDate(r.createdAt)}
                        </div>
                        {r.receiptNumber && (
                          <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                            {r.receiptNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <PayoutAvatar
                            name={name}
                            tone="primary"
                            className="size-7 text-[10px]"
                          />
                          <div className="min-w-0">
                            <div className="truncate font-medium leading-tight text-foreground">
                              {name}
                            </div>
                            <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                              {METHOD_LABEL[r.paymentMethod]}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <div className="leading-tight text-foreground">
                          {PLAN_LABEL[r.planType]}
                        </div>
                        <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                          {fmtDate(r.periodStart)} - {fmtDate(r.periodEnd)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right">
                        <MoneyValue
                          value={r.amountGross}
                          currency={r.currency}
                          className="font-semibold"
                        />
                        <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                          Net {r.amountNet.toFixed(2)}
                          <span className="mx-1 text-muted-foreground/50">·</span>
                          PDV {r.amountVat.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <Pill
                          label={STATUS_BADGE[r.status].label}
                          tone={STATUS_BADGE[r.status].tone}
                        />
                      </td>
                      <td className="py-3 pl-1 pr-4 text-right">
                        {r.status === "completed" && <PdfLink ledgerId={r.id} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet cards */}
          <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
            {rows.map((r) => {
              const name = r.therapistName ?? r.therapistFullName;
              return (
                <div
                  key={r.id}
                  className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <PayoutAvatar
                        name={name}
                        tone="primary"
                        className="size-8 text-[10px]"
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium leading-tight text-foreground">
                          {name}
                        </div>
                        <div className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                          {fmtDate(r.createdAt)}
                          {r.receiptNumber && (
                            <>
                              <span className="mx-1 text-muted-foreground/50">
                                ·
                              </span>
                              <span className="font-mono">
                                {r.receiptNumber}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Pill
                      label={STATUS_BADGE[r.status].label}
                      tone={STATUS_BADGE[r.status].tone}
                    />
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border/40 pt-3">
                    <MobileField label="Plan">
                      {PLAN_LABEL[r.planType]}
                    </MobileField>
                    <MobileField label="Način">
                      {METHOD_LABEL[r.paymentMethod]}
                    </MobileField>
                    <MobileField label="Period">
                      <span className="tabular-nums">
                        {fmtDate(r.periodStart)} - {fmtDate(r.periodEnd)}
                      </span>
                    </MobileField>
                    <MobileField label="Bruto">
                      <MoneyValue
                        value={r.amountGross}
                        currency={r.currency}
                        className="font-semibold"
                      />
                    </MobileField>
                  </div>

                  {r.status === "completed" && (
                    <div className="mt-3 flex justify-end border-t border-border/40 pt-3">
                      <PdfLink ledgerId={r.id} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function PdfLink({ ledgerId }: { ledgerId: string }) {
  return (
    <a
      href={`/api/subscription/receipts/${ledgerId}/pdf`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
    >
      <FilePdf className="size-4" />
      PDF
    </a>
  );
}
